import { Providers } from '@microsoft/mgt-element';
import * as microsoftTeams from '@microsoft/teams-js';
import axios from 'axios';
import { IDBPDatabase, openDB } from 'idb';
import { WipeData } from '../../user/UserForm';

/**
 * One shared connection to the photo database, opened lazily.
 *
 * No version pin - the store list is owned by cache/UserPhoto.tsx, and opening with a lower
 * version than the database already has throws. #34542
 *
 * A failed open is not memoised, so a transient failure can be retried on the next call.
 */
let photoDbPromise: Promise<IDBPDatabase> | null = null;

const photoDatabase = (): Promise<IDBPDatabase> => {
    photoDbPromise ??= openDB("mgt-photos").catch(error => {
        photoDbPromise = null;
        throw error;
    });

    return photoDbPromise;
};

export interface DownloadableAttachment {
    DriveItemId?: string;
    DriveId?: string;
    DriveItemGroupId?: string;
}

export class graphService  {
    api_url: string | undefined;

    constructor() {
      this.api_url = "https://graph.microsoft.com/v1.0";
    }

    init = (customHeaders?, customConfig?) => {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken)
            Providers.globalProvider.login();

        let headers = {
            "Accept": "application/json",
            "Authorization": `Bearer ${accessToken}`,
            "Access-Control-Allow-Origin":  "*"
        };
        headers = Object.assign(headers, customHeaders);
        let config = {
            baseURL: this.api_url,
            timeout: 31000,
            headers: headers
        };
        config = Object.assign(config, customConfig);
        // The interceptor pages on the client that made the request, so concurrent calls through one service instance cannot cross. 
        const client = axios.create(config);

        client.interceptors.response.use(
            async response => {
              if (response.data && response.data['@odata.nextLink']) {
                const url = new URL(response.data['@odata.nextLink']);
                const req = {
                  ...response.config,
                  url: url.toString()
                };
                const rsp = await client.request(req);
                response.data.value = response.data.value.concat(rsp.data.value);
              }
              return response;
            },
            error => Promise.reject(error)
          );

        return client;
    };
    
    getManagedDevices = (usersId: string) => {
        return this.init().get(`/users/${usersId}/managedDevices`).then((response) => {
            return response;
        });
    }

    getOwnedDevices = (usersId: string) => {
        return this.init().get(`/users/${usersId}/ownedDevices`).then((response) => {
            return response;
        })
    }

    getMeetingDetails = (context: microsoftTeams.app.Context) => {
        return this.init().get(`/me/events`).then((response) => {
            var events = response.data.value;
            var currentEvent = events.filter((x) => {
                if(x.isOnlineMeeting){
                    const joinUrl = x.onlineMeeting.joinUrl || "";
                    const chatId = new URLSearchParams(context.chat.id).toString().toLowerCase();
                    return joinUrl.toLowerCase().indexOf(chatId) > -1;
                }
        
                return false;
            });
            return currentEvent[0];
        });
    }

    updateMeetingDetails = (ticketId: number, event: {}) => {

        if(!event["isOrganizer"]){
            return new Promise(resolve => {
                return false;
            });
        }
        
        var eventId = event["id"] || 0;
        var eventBody = event["body"]["content"] || "";
        var endOfBodyIndex = eventBody.indexOf("<body>");
        var insertTicketId = eventBody.slice(0, endOfBodyIndex) + `<div>---- Do not remove this line: [{​​​"TicketId":${ticketId} }] ----</div>` + eventBody.slice(endOfBodyIndex);
        var data = {
            body: {
                "content": insertTicketId,
                "contentType": "html"
            }
        }

        return this.init().patch(`/me/events/${eventId}`, data);
    }

    getUserList = () => {
        return this.init().get(`/users`);
    }

    getUser = (userId: string, params?: string) => {
        return this.init().get(`/users/${userId}${params ? (params) : ''} `);
    }

    getMembersOfGroup = (groupId: string) => {
        return this.init().get(`/groups/${groupId}/members`);
    }
    
    getUserApps = (userId: string) => {
        return this.init().get(`/users/${userId}/teamwork/installedApps?$expand=teamsApp,teamsAppDefinition&$filter=teamsApp/externalId in ('${window.__runtimeConfig.tikitClientId}','${window.__runtimeConfig.helpbotClientId}','${window.__runtimeConfig.tvaClientId}') or teamsApp/id in ('${window.__runtimeConfig.tikitClientId}','${window.__runtimeConfig.helpbotClientId}', '${window.__runtimeConfig.tvaClientId}')`);
    }

    getTeamApps = (teamId: string) => {
        return this.init().get(`/teams/${teamId}/installedApps?$expand=teamsApp,teamsAppDefinition&$filter=teamsApp/externalId eq '${window.__runtimeConfig.tikitClientId}' or teamsApp/id eq '${window.__runtimeConfig.tikitClientId}'`);
    }

    getCatalogApps = (userId: string) => {
        return this.init().get(`/appCatalogs/teamsApps?$expand=appDefinitions&$filter=externalId in ('${window.__runtimeConfig.tikitClientId}','${window.__runtimeConfig.helpbotClientId}','${window.__runtimeConfig.tvaClientId}') or id in ('${window.__runtimeConfig.tikitClientId}','${window.__runtimeConfig.helpbotClientId}', '${window.__runtimeConfig.tvaClientId}')`);
    }

    installUserApp = (userId: string, teamsAppId: string) => {
        var data = {
            "teamsApp@odata.bind":`https://graph.microsoft.com/v1.0/appCatalogs/teamsApps/${teamsAppId}`
        }
        return this.init().post(`/users/${userId}/teamwork/installedApps`, data);
    }

    uninstallUserApp = (userId: string, appInstallId: string) => {
        return this.init().delete(`/users/${userId}/teamwork/installedApps/${appInstallId}`);
    }

    updateUserApp = (userId: string, appInstallId: string) => {
        return this.init().post(`/users/${userId}/teamwork/installedApps/${appInstallId}/upgrade`);
    }

    getUserPhoto = async (idOrUpn: string) => {
        // Shared handle: this runs once per avatar, and opening the database per call had the list
        // issuing dozens of concurrent opens that each took seconds and starved the other stores'
        // transactions — including the feature and user lookups the list blocks on.
        const db = await photoDatabase();
        const data = await db.get("users", idOrUpn);
        if (data?.photo)
        {
            return data.photo;
        }
        else
        {
            const image = await this.init({"Content-Type": "image/jpg"}, {responseType: "arraybuffer"}).get(`/users/${idOrUpn}/photo/$value`);
            const base64 = Buffer.from(image.data, 'binary').toString('base64');
            return `data:image/jpeg;base64, ${base64}`;
        }
    }

    // Teams and Entra groups are both backed by an M365 group, so their photo
    // lives under /groups rather than /users. #34542
    getGroupPhoto = async (id: string) => {
        const image = await this.init({"Content-Type": "image/jpg"}, {responseType: "arraybuffer"}).get(`/groups/${id}/photo/$value`);
        // A group with no picture can answer 200 with an empty body. Returning a
        // payload free data URI would read as "has a photo" and render a broken
        // image, so treat it as no photo. #34542
        if (!image?.data || image.data.byteLength === 0) return "";
        const base64 = Buffer.from(image.data, "binary").toString("base64");
        return base64.length === 0 ? "" : `data:image/jpeg;base64,${base64}`;
    }

    getUserPresence = (id: string) => {
        return this.init().get(`/users/${id}/presence`).then((result) => {
           return result.data;
        }).catch(() => {
            return { availability: "PresenceUnknown", activity: "Unknown"};
        });
    }

    getMePeople = () => {
        return this.init().get(`/me/people?$filter=personType/class eq 'Person' and personType/subclass eq 'OrganizationUser'`);
    }

    /**
     * A channel-scoped file lives in the channel's own drive rather than the group's default drive, so the
     * drive id is the only thing that can reach it. A file predating channel-scoped storage carries no drive
     * id and is still addressed through its group, which is why both forms are kept.
     *
     * Takes the attachment rather than an id, so that a caller cannot pass a group id where a drive id is
     * meant - the two are the same type and were previously in the same position.
     */
    downloadFile = (attachment: DownloadableAttachment) => {
        const itemId = attachment?.DriveItemId;
        const driveId = attachment?.DriveId;
        const groupId = attachment?.DriveItemGroupId;

        if (!itemId || (!driveId && !groupId)) {
            console.error('downloadFile: attachment cannot be located', { itemId, driveId, groupId });
            return Promise.resolve(false);
        }

        const path = driveId
            ? `/drives/${driveId}/items/${itemId}`
            : `/groups/${groupId}/drive/items/${itemId}`;

        return this.init().get(path).then((response) => {
            var element = document.createElement('a');
            element.setAttribute('href', response.data["@microsoft.graph.downloadUrl"]);

            element.style.display = 'none';
            document.body.appendChild(element);

            element.click();

            document.body.removeChild(element);
            return true;
        }).catch((error) => {
            // Returning false silently is what turned a broken download into an invisible one.
            console.error(`downloadFile: GET ${path} failed`, error);
            return false;
        });
    }

    // managed device

    
    retireDevice = (managedDeviceId: string) => {
        return this.init().post(`/deviceManagement/managedDevices('${managedDeviceId}')/retire`).then((response) => {
            return response;
        });
    }

    wipeDevice = (managedDeviceId: string, data: WipeData) => {
        return this.init().post(`/deviceManagement/managedDevices('${managedDeviceId}')/wipe`, data).then((response) => {
            return response;
        });
    }
    
    remoteLockDevice = (managedDeviceId: string) => {
        return this.init().post(`/deviceManagement/managedDevices('${managedDeviceId}')/remoteLock`).then((response) => {
            return response;
        });
    }
    
    rebootNowDevice = (managedDeviceId: string) => {
        return this.init().post(`/deviceManagement/managedDevices('${managedDeviceId}')/rebootNow`).then((response) => {
            return response;
        });
    }

    me = (params?: string) => {
        return this.init().get(`/me/memberOf${params ? params : ''}`).then((response) => {
            return response;
        })
    }

    getEffectivePermissions = () => {
        return this.init().get(`/deviceManagement/getEffectivePermissions(scope='*')`).then((response) => {
            return response;
        })
    }

    installTeamApp = (teamId: string, teamsAppId: string) => {
        const data = {
            "teamsApp@odata.bind":`https://graph.microsoft.com/v1.0/appCatalogs/teamsApps/${teamsAppId}`
        }
        return this.init().post(`/teams/${teamId}/installedApps`, data);
    }

    getTeamData = (teamId) => {
        return this.init().get(`/teams/${teamId}`);
    }

    getJoinedTeams = (params?: string) => {
        return this.init().get(`/me/joinedTeams${params ? params : ''}`);
    }

    // Deliberately by id. Listing /groups pages the whole tenant directory through the
    // nextLink interceptor above - 200-350 sequential requests and tens of MB on a large
    // tenant - so resolve the few groups actually referenced instead of enumerating.
    getGroupById = (groupId: string) => this.init().get(`/groups/${groupId}?$select=id,displayName`);

    getTeamMembers = (teamId: string) => this.init().get(`/groups/${teamId}/members?$select=id,displayName,mail,userPrincipalName`);

    getChannelData = async (data: { teamId: string, channelId: string }[]) => {
        try {
            // Get distinct
            const requests = data.reduce((r, c) => {
                if (!r.find(x => x.id === c.channelId))
                    r.push({ url: `/teams/${c.teamId}/channels/${c.channelId}`, method: "GET", id: c.channelId });
                
                return r;
             }, []);
            const results = await this.init().post('/$batch', { requests: requests });
            
            return data.map(c => ({
                ...c,
                ...results.data.responses.find(x => x.id === c.channelId).body
            }));
        } catch {
            return data;
        }
    }
}